import React, { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import './ResetPassword.css';

const SUPPORT_EMAIL = 'mohammedrazachandwala@gmail.com';
const MIN_PASSWORD_LENGTH = 8;

type Step = 'verifying' | 'form' | 'success' | 'invalid';

// Defined at module level (not inside ResetPassword) so its component identity
// is stable across re-renders. Defining it inline would remount the whole tree
// on every keystroke, making the inputs lose focus.
const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="rp-root">
    <header className="rp-header">
      <div className="rp-header-inner">
        <div className="rp-logo">
          <img src="/app_logo.png" alt="BasniConnect" className="rp-logo-img" />
          <span className="rp-logo-text">BasniConnect</span>
        </div>
        <span className="rp-badge">Account Security</span>
      </div>
    </header>

    <div className="rp-center">{children}</div>

    <footer className="rp-footer">
      <div className="rp-footer-inner">
        <div className="rp-footer-logo">
          <img src="/app_logo.png" alt="BasniConnect" className="rp-logo-img rp-logo-img--sm" />
          <span className="rp-logo-text">BasniConnect</span>
        </div>
        <p className="rp-footer-text">
          © {new Date().getFullYear()} BasniConnect. All rights reserved.
        </p>
      </div>
    </footer>
  </div>
);

const ResetPassword: React.FC = () => {
  const [step, setStep] = useState<Step>('verifying');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState('');
  const [loading, setLoading] = useState(false);

  // On mount: establish a recovery session from the reset link, then let the
  // user set a new password. We decide purely from the return value of the
  // auth call (setSession / verifyOtp / exchangeCodeForSession) — NOT from a
  // follow-up getSession() or an auth event, so there is no timing race and it
  // works on the very first load.
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      // Capture the link's tokens before anything strips the URL.
      const query = new URLSearchParams(window.location.search);
      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const accessToken = hash.get('access_token');
      const refreshToken = hash.get('refresh_token');
      const tokenHash = query.get('token_hash');
      const code = query.get('code');

      let session = null;
      let error: { message: string } | null = null;

      if (accessToken && refreshToken) {
        const res = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        session = res.data.session;
        error = res.error;
      } else if (tokenHash) {
        const res = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'recovery' });
        session = res.data.session;
        error = res.error;
      } else if (code) {
        const res = await supabase.auth.exchangeCodeForSession(code);
        session = res.data.session;
        error = res.error;
      } else {
        // No token in the URL — maybe a session is already stored.
        const res = await supabase.auth.getSession();
        session = res.data.session;
      }

      if (cancelled) return;

      if (error) {
        // eslint-disable-next-line no-console
        console.error('Reset link verification failed:', error.message);
        setStep('invalid');
        return;
      }
      if (!session) {
        setStep('invalid');
        return;
      }

      // Strip the token (query + hash) from the URL so a refresh can't reuse it.
      window.history.replaceState({}, '', window.location.pathname);
      setStep('form');
    };

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!password) e.password = 'Please enter a new password.';
    else if (password.length < MIN_PASSWORD_LENGTH)
      e.password = `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
    if (!confirm) e.confirm = 'Please confirm your new password.';
    else if (password && confirm !== password) e.confirm = 'Passwords do not match.';
    return e;
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    const e = validate();
    if (Object.keys(e).length > 0) {
      setErrors(e);
      return;
    }
    setErrors({});
    setApiError('');
    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        setApiError(error.message || 'Could not update your password. Please try again.');
      } else {
        setStep('success');
      }
    } catch {
      setApiError('Unable to connect. Please check your internet and try again.');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'verifying') {
    return (
      <Shell>
        <div className="rp-card rp-card--status">
          <div className="rp-spinner" aria-hidden="true" />
          <h2 className="rp-status-title">Verifying your link…</h2>
          <p className="rp-status-text">Hang tight while we confirm your reset link.</p>
        </div>
      </Shell>
    );
  }

  if (step === 'invalid') {
    return (
      <Shell>
        <div className="rp-card rp-card--status">
          <div className="rp-icon rp-icon--warn">!</div>
          <h2 className="rp-status-title">Link Invalid or Expired</h2>
          <p className="rp-status-text">
            This password reset link is invalid or has already been used. Reset links expire
            after a short time for your security.
          </p>
          <p className="rp-status-note">
            Please open the BasniConnect app and request a new password reset link. If you keep
            having trouble, contact us at{' '}
            <a className="rp-link" href={`mailto:${SUPPORT_EMAIL}`}>
              {SUPPORT_EMAIL}
            </a>
            .
          </p>
        </div>
      </Shell>
    );
  }

  if (step === 'success') {
    return (
      <Shell>
        <div className="rp-card rp-card--status">
          <div className="rp-icon rp-icon--ok">✓</div>
          <h2 className="rp-status-title">Password Updated</h2>
          <p className="rp-status-text">
            Your password has been changed successfully.
          </p>
          <p className="rp-status-note">
            You can now return to the <strong>BasniConnect</strong> app and sign in with your
            new password.
          </p>
        </div>
      </Shell>
    );
  }

  // step === 'form'
  return (
    <Shell>
      <div className="rp-card">
        <div className="rp-card-head">
          <h1 className="rp-title">Set a New Password</h1>
          <p className="rp-subtitle">
            Choose a strong password you haven't used before. It must be at least{' '}
            {MIN_PASSWORD_LENGTH} characters.
          </p>
        </div>

        <form className="rp-form" onSubmit={handleSubmit} noValidate>
          <div className="rp-form-group">
            <label className="rp-label" htmlFor="password">
              New Password <span className="rp-required">*</span>
            </label>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              className={`rp-input ${errors.password ? 'rp-input--error' : ''}`}
              placeholder="Enter a new password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              autoComplete="new-password"
            />
            {errors.password && <p className="rp-error">{errors.password}</p>}
          </div>

          <div className="rp-form-group">
            <label className="rp-label" htmlFor="confirm">
              Confirm New Password <span className="rp-required">*</span>
            </label>
            <input
              id="confirm"
              type={showPassword ? 'text' : 'password'}
              className={`rp-input ${errors.confirm ? 'rp-input--error' : ''}`}
              placeholder="Re-enter your new password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              disabled={loading}
              autoComplete="new-password"
            />
            {errors.confirm && <p className="rp-error">{errors.confirm}</p>}
          </div>

          <label className="rp-show-label">
            <input
              type="checkbox"
              className="rp-checkbox"
              checked={showPassword}
              onChange={(e) => setShowPassword(e.target.checked)}
              disabled={loading}
            />
            <span className="rp-show-text">Show password</span>
          </label>

          {apiError && <p className="rp-error rp-error--banner">{apiError}</p>}

          <button type="submit" className="rp-submit-btn" disabled={loading}>
            {loading ? 'Updating…' : 'Update Password'}
          </button>
        </form>
      </div>
    </Shell>
  );
};

export default ResetPassword;
